import { create } from "zustand";

// Run E2 J2: one shared UI language for the site chrome (header, footer,
// menus, buttons) and the pages rebuilt for K-5. Spanish mode is set by:
//   - the header EN/ES switch,
//   - the /curriculum "English / Español" buttons,
//   - a ?lang=es (or ?lang=en) query param on page load,
//   - opening a student lesson assigned in Spanish (pe:<ch>:<tier>:es).
// The choice is kept in localStorage ("pe_lang") so it survives reloads.

export type UiLang = "en" | "es";

const STORAGE_KEY = "pe_lang";

function readInitialLang(): UiLang {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("lang");
    if (fromUrl === "es" || fromUrl === "en") return fromUrl;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "es" || stored === "en") return stored;
  } catch {
    // localStorage can be blocked; fall back to English.
  }
  return "en";
}

interface UiLangState {
  lang: UiLang;
  setLang: (lang: UiLang) => void;
}

const initialLang = readInitialLang();
if (typeof document !== "undefined") document.documentElement.lang = initialLang;

export const useUiLangStore = create<UiLangState>()((set) => ({
  lang: initialLang,
  setLang: (lang) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore
    }
    if (typeof document !== "undefined") document.documentElement.lang = lang;
    set({ lang });
  },
}));

/** Current UI language. */
export function useUiLang(): UiLang {
  return useUiLangStore((s) => s.lang);
}

/** Pick the string table for the current UI language. */
export function useT<T>(table: Record<UiLang, T>): T {
  return table[useUiLang()];
}
